import styled from "@emotion/styled"
import { css } from '@emotion/react'
import facepaint from "facepaint"

import { ReactComponent as LineLongSvg } from 'resources/illustrations/lines/product-long-line.svg'
import { ReactComponent as LineShortSvg } from 'resources/illustrations/lines/product-short-line.svg'
import { ReactComponent as AsteriksSvg } from 'resources/icons/general/asteriks_pt1.svg'

const breakpoints = [440, 768, 1024, 1200, 1400]
const mq = facepaint(breakpoints.map(bp => `@media (min-width: ${bp}px)`))

const wrapMQ = css(mq({
  marginTop: [
    32, // 0+
    32, // 440+
    64, // 768+
    80, // 1024+
    80, // 1200+
    116, // 1400+
  ],
  padding: [
    "44px 0", // 0+
    "44px 0", // 440+
    "48px 0", // 768+
    "70px 0", // 1024+
    "70px 0", // 1200+
    "120px 0 98px", // 1400+
  ],
}))

export const Wrap = styled.div`
  background: var(--color-grayscale-900);
  ${wrapMQ}
`

const contentMQ = css(mq({
  maxWidth: [
    "calc(100vw - 32px)", // 0+
    "calc(100vw - 32px)", // 440+
    "calc(100vw - 64px)", // 768+
    "calc(100vw - 80px)", // 1024+
  ],
  gap: [
    28, // 0+
    28, // 440+
    28, // 768+
    24, // 1024+
    64, // 1200+
    64, // 1400+
  ],
}))

export const Content = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 1260px;
  margin: 0 auto;
  ${contentMQ}
`

const titleMQ = css(mq({
  font: [
    "var(--accent-770)", // 0+
    "var(--accent-770)", // 440+
    "var(--accent-970)", // 768+
  ],
}))

export const Title = styled.h2`
  color: var(--color-white);
  ${titleMQ}
`

const integrationsWrapMQ = css(mq({
  gridTemplateRows: [
    "auto auto auto", // 0+
    "auto auto auto", // 440+
    "auto auto auto", // 768+
    "auto auto auto", // 1024+
    "auto 88px auto 88px auto", // 1200+
    "auto 88px auto 88px auto", // 1400+
  ],
  gap: [
    24, // 0+
    24, // 440+
    24, // 768+
    24, // 1024+
    0, // 1200+
    0, // 1400+
  ]
}))

export const IntegrationsWrap = styled.div`
  display: grid;
  ${integrationsWrapMQ}
`

const productListMQ = css(mq({
  gridTemplateColumns: [
    "repeat(auto-fit, minmax(240px, 1fr))", // 0+
    "repeat(auto-fit, minmax(240px, 1fr))", // 440+
    "1fr 1fr", // 768+
    "1fr 1fr", // 1024+
    "1fr 1fr 1fr 1fr", // 1200+
    "1fr 1fr 1fr 1fr", // 1400+
  ],
  background: [
    "#36373C", // 0+
    "#36373C", // 440+
    "none", // 768+
    "none", // 1024+
    "none", // 1200+
    "none", // 1400+
  ],
  padding: [
    "24px 20px", // 0+
    "24px 20px", // 440+
    0, // 768+
  ],
  borderRadius: [
    20, // 0+
    20, // 440+
    0, // 768+
  ],
  gap: [
    12, // 0+
    12, // 440+
    24, // 768+
    24, // 1024+
    20, // 1200+
    20, // 1400+
  ],
}))

export const ProductList = styled.div`
  display: grid;
  ${productListMQ}
`

const productItemMQ = css(mq({
  flexDirection: [
    "row", // 0+
    "row", // 440+
    "column", // 768+
    "row", // 1024+
    "column", // 1200+
    "column", // 1400+
  ],

  justifyContent: [
    "normal", // 0+
    "normal", // 440+
    "center", // 768+
  ],
  outline: [
    "none", // 0+
    "none", // 440+
    "1px solid var(--color-grayscale-750)", // 768+
    "1px solid var(--color-grayscale-750)", // 1024+
    "1px solid var(--color-grayscale-750)", // 1200+
    "1px solid var(--color-grayscale-750)", // 1400+
  ],
  background: [
    "none", // 0+
    "none", // 440+
    "none", // 768+
    "none", // 1024+
    "var(--color-grayscale-900)", // 1200+
    "var(--color-grayscale-900)", // 1400+
  ],
  padding: [
    "5px 0", // 0+
    "5px 0", // 440+
    "18px 24px 12px", // 768+
    "20px 24px 20px", // 1024+
    "18px 24px 12px", // 1200+
    "18px 24px 12px", // 1400+
  ],
  gap: [
    12, // 0+
    12, // 440+
    12, // 768+
    20, // 1024+
    8, // 1200+
    8, // 1400+
  ],
  "&:before": {
    display: [
      "none", // 0+
      "none", // 440+
      "none", // 768+
      "none", // 1024+
      "block", // 1200+
      "block", // 1400+
    ],
  }
}))

export const ProductItem = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  color: var(--color-white);
  font: var(--text-640);
  outline-offset: -1px;
  border-radius: 20px;

  &:before {
    position: absolute;
    content: "";
    bottom: -4px;
    left: 50%;
    width: 9px;
    height: 9px;
    border: 1px solid #7F7678;
    background: var(--color-grayscale-900);
    border-radius: 8px;
    transform: translateX(-50%);
  }
  ${productItemMQ}
`

export const ProductLogos = styled.div`
  display: flex;

  & > div:nth-of-type(2) {
    margin-left: -12px;
    z-index: 1;
  }
`

const productLogoCircleMQ = css(mq({
  background: [
    "#36373C", // 0+
    "#36373C", // 440+
    "var(--color-grayscale-900)", // 768+
    "var(--color-grayscale-900)", // 1024+
    "var(--color-grayscale-900)", // 1200+
    "var(--color-grayscale-900)", // 1400+
  ],
}))

export const AsteriksIcon = styled(AsteriksSvg)`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 3;
  color: var(--color-grayscale-700);
  color: var(--color-grayscale-700);
  padding: 2px;
  width: 16px;
  height: 16px;
  border-radius: 12px;
  ${productLogoCircleMQ}
`

export const ProductLogoCircle = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed var(--color-grayscale-700);
  outline: 1px solid var(--color-grayscale-700);
  outline-offset: -5px;
  border-radius: 54px;
  width: 54px;
  height: 54px;
  ${productLogoCircleMQ}
`

const linesMQ = css(mq({
  display: [
    "none", // 0+
    "none", // 440+
    "none", // 768+
    "none", // 1024+
    "block", // 1200+
    "block", // 1400+
  ],
}))

export const Lines = styled.div`
  position: relative;
  ${linesMQ}
`

export const LineTopLeftLong = styled(LineLongSvg)`
  --left: calc((((100% - 60px) / 4) / 2) - 1px);
  position: absolute;
  top: 0;
  left: var(--left);
  width: calc((100% / 2) - var(--left));
  transform: rotateY(180deg);
`

export const LineTopLeftShort = styled(LineShortSvg)`
  --gap: 20px;
  --all-gaps: calc(var(--gap) * 3);
  --block-width: calc((100% - var(--all-gaps)) / 4);
  --left: calc(((100% / 2) - (var(--gap) / 2) - (var(--block-width) / 2)) - 1px);
  position: absolute;
  top: 0;
  left: var(--left);
  height: 132px;
  width: calc((100% / 2) - var(--left));
  transform: rotateY(180deg);
`

export const LineTopRightLong = styled(LineLongSvg)`
  --right: calc((((100% - 60px) / 4) / 2) - 1px);
  position: absolute;
  top: 0;
  right: var(--right);
  height: 128px;
  width: calc((100% / 2) - var(--right));
}
`

export const LineTopRightShort = styled(LineShortSvg)`
  --gap: 20px;
  --all-gaps: calc(var(--gap) * 3);
  --block-width: calc((100% - var(--all-gaps)) / 4);
  --right: calc(((100% / 2) - (var(--gap) / 2) - (var(--block-width) / 2)) - 1px);
  position: absolute;
  top: -36px;
  right: var(--right);
  height: 152px;
  width: calc((100% / 2) - var(--right));
`

const mainAreaMQ = css(mq({
  gridTemplateColumns: [
    "unset", // 0+
    "unset", // 440+
    "1fr 1fr", // 768+
    "1fr 1fr", // 1024+
    "auto auto auto", // 1200+
    "auto auto auto", // 1400+
  ],
  gap: [
    24, // 0+
    24, // 440+
    24, // 768+
    24, // 1024+
    44, // 1200+
    44, // 1400+
  ],
  alignItems: [
    "normal", // 0+
    "normal", // 440+
    "normal", // 768+
    "normal", // 1024+
    "center", // 1200+
    "center", // 1400+
  ],
}))

export const MainArea = styled.div`
  display: grid;
  justify-content: center;
  position: relative;
  z-index: 1;

  & > div:nth-of-type(1):before {
    right: -12px;
  }

  & > div:nth-of-type(3):before {
    left: -12px;
  }
  ${mainAreaMQ}
`

const scopeBlockMQ = css(mq({
  display: [
    "none", // 0+
    "none", // 440+
    "none", // 768+
    "none", // 1024+
    "flex", // 1200+
    "flex", // 1400+
  ],
}))

export const ScopeBlock = styled.div`
  display: flex;
  position: relative;
  z-index: 1;

  &:before {
    position: absolute;
    content: "";
    top: 0;
    left: 50%;
    width: 9px;
    height: 9px;
    border: 1px solid var(--color-grayscale-200);
    background: var(--color-grayscale-900);
    border-radius: 8px;
    transform: translate(-50%, -50%);
  }

  &:after {
    position: absolute;
    content: "";
    bottom: 0;
    left: 50%;
    width: 9px;
    height: 9px;
    border: 1px solid var(--color-grayscale-200);
    background: var(--color-grayscale-900);
    border-radius: 8px;
    transform: translate(-50%, 50%);
  }
  ${scopeBlockMQ}
`

export const LineBottomLeftLong = styled(LineLongSvg)`
  --left: calc((((100% - 60px) / 4) / 2) - 1px);
  position: absolute;
  top: -26px;
  left: var(--left);
  width: calc((100% / 2) - var(--left));
  transform: rotateY(180deg) rotateX(180deg);
`

export const LineBottomLeftShort = styled(LineShortSvg)`
  --gap: 20px;
  --all-gaps: calc(var(--gap) * 3);
  --block-width: calc((100% - var(--all-gaps)) / 4);
  --left: calc(((100% / 2) - (var(--gap) / 2) - (var(--block-width) / 2)) - 1px);
  position: absolute;
  top: -22px;
  left: var(--left);
  height: 132px;
  width: calc((100% / 2) - var(--left));
  transform: rotateY(180deg) rotateX(180deg);
`

export const LineBottomRightLong = styled(LineLongSvg)`
  --right: calc((((100% - 60px) / 4) / 2) - 1px);
  position: absolute;
  top: -28px;
  right: var(--right);
  height: 128px;
  width: calc((100% / 2) - var(--right));
  transform: rotateX(180deg);
}
`

export const LineBottomRightShort = styled(LineShortSvg)`
  --gap: 20px;
  --all-gaps: calc(var(--gap) * 3);
  --block-width: calc((100% - var(--all-gaps)) / 4);
  --right: calc(((100% / 2) - (var(--gap) / 2) - (var(--block-width) / 2)) - 1px);
  position: absolute;
  top: -22px;
  right: var(--right);
  height: 152px;
  width: calc((100% / 2) - var(--right));
  transform: rotateX(180deg);
`

const messageBlockMQ = css(mq({
  width: [
    "auto", // 0+
    "auto", // 440+
    "auto", // 768+
    "auto", // 1024+
    370, // 1200+
    370, // 1400+
  ],
  padding: [
    "24px 32px", // 0+
    "24px 32px", // 440+
    "32px 40px", // 768+
    "24px", // 1024+
    "32px 40px", // 1200+
    "32px 40px", // 1400+
  ],
  flexDirection: [
    "column", // 0+
    "column", // 440+
    "column", // 768+
    "row", // 1024+
    "column", // 1200+
    "column", // 1400+
  ],
  alignItems: [
    "start", // 0+
    "start", // 440+
    "start", // 768+
    "start", // 1024+
    "center", // 1200+
    "center", // 1400+
  ],
  textAlign: [
    "left", // 0+
    "left", // 440+
    "left", // 768+
    "left", // 1024+
    "center", // 1200+
    "center", // 1400+
  ],
  "&:before, &:after": {
    display: [
      "none", // 0+
      "none", // 440+
      "none", // 768+
      "none", // 1024+
      "flex", // 1200+
      "flex", // 1400+
    ],
  }
}))

export const MessageBlock = styled.div`
  position: relative;
  display: flex;
  gap: 20px;
  color: var(--color-grayscale-200);
  background: #36373C;
  border-radius: 24px;
  font: var(--text-340);

  &:before {
    content: '';
    position: absolute;
    top: 50%;
    width: 36px;
    height: 36px;
    border-radius: 0 6px 0 6px;
    transform: translateY(-50%) rotate(45deg);
    background: #36373C;
  }
  ${messageBlockMQ}
`

const useCasesMQ = css(mq({
  gridTemplateColumns: [
    "repeat(auto-fit, minmax(240px, 1fr))", // 0+
    "repeat(auto-fit, minmax(240px, 1fr))", // 440+
    "1fr 1fr", // 768+
    "1fr 1fr", // 1024+
    "1fr 1fr 1fr 1fr", // 1200+
    "1fr 1fr 1fr 1fr", // 1400+
  ],
  gap: [
    12, // 0+
    12, // 440+
    12, // 768+
    12, // 1024+
    20, // 1200+
    20, // 1400+
  ],
}))

export const UseCases = styled.div`
  display: grid;
  ${useCasesMQ}
`

export const CaseIcon = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 16px;
  border: 1px dashed rgba(255, 255, 255, 0.33);
  outline-style: solid;
  outline-width: 1px;
  outline-offset: -5px;
  outline-color: rgba(255, 255, 255, 0.33);

  & > svg {
    width: 20px;
    height: 20px;
    color: var(--color-white);
  }
`

const caseMQ = css(mq({
  display: [
    "list-item", // 0+
    "list-item", // 440+
    "list-item", // 768+
    "list-item", // 1024+
    "flex", // 1200+
    "flex", // 1400+
  ],
  marginLeft: [
    18, // 0+
    18, // 440+
    18, // 768+
    18, // 1024+
    0, // 1200+
    0, // 1400+
  ],
  alignItems: [
    "start", // 0+
    "start", // 440+
    "start", // 768+
    "start", // 1024+
    "center", // 1200+
    "center", // 1400+
  ],
  textAlign: [
    "left", // 0+
    "left", // 440+
    "left", // 768+
    "left", // 1024+
    "center", // 1200+
    "center", // 1400+
  ],
  outline: [
    "none", // 0+
    "none", // 440+
    "none", // 768+
    "none", // 1024+
    "1px solid var(--color-grayscale-750)", // 1200+
    "1px solid var(--color-grayscale-750)", // 1400+
  ],
  background: [
    "none", // 0+
    "none", // 440+
    "none", // 768+
    "none", // 1024+
    "var(--color-grayscale-900)", // 1200+
    "var(--color-grayscale-900)", // 1400+
  ],
  padding: [
    "0px", // 0+
    "0px", // 440+
    "0px", // 768+
    "0px", // 1024+
    "16px 24px", // 1200+
    "16px 24px", // 1400+
  ],
  "&:before": {
    display: [
      "none", // 0+
      "none", // 440+
      "none", // 768+
      "none", // 1024+
      "block", // 1200+
      "block", // 1400+
    ],
  }
}))

export const Case = styled.div`
  position: relative;
  flex-direction: column;
  color: var(--color-grayscale-37);
  font: var(--text-540);
  outline-offset: -1px;
  border-radius: 20px;

  &:before {
    position: absolute;
    content: "";
    top: -4px;
    left: 50%;
    width: 9px;
    height: 9px;
    border: 1px solid #7F7678;
    background: var(--color-grayscale-900);
    border-radius: 8px;
    transform: translateX(-50%);
  }
  ${caseMQ}
`

export const Disclaimer = styled.div`
  position: absolute;
  right: 0;
  bottom: -42px;
  font: var(--text-240);
  color: var(--color-grayscale-400)
`
